/**
 * 画布设置：尺寸（viewBox）、比例预设、背景色。
 *
 * 语义关键：**改画布尺寸只动画板，内容尺寸不变**（扩画布 = 留白）。
 * 因此只改 `viewBox` 的宽高、保持 min-x/min-y 与所有元素坐标不动。
 * （曾经试过"内容跟着画板一起缩放"，被否：那不是等比放大吗 —— 用户要的只是画布变大变小。）
 *
 * ⚠️ 配套：命令执行后必须重排承载 svg 的容器（见 useEditor.bump）——
 * 画板在屏幕上的尺寸是由 viewBox 推出来的，不重排就会出现
 * "字段改了、屏幕上一动不动"的假象（真踩过）。
 */
import { pathOf } from '../dom';
import type { Rect } from '../geom/matrix';
import type { Command } from '../history';
import { attrPatch, type Patch } from '../patch';
import { parseStyleAttr, styleValue } from '../style-attr';

export interface CanvasSize {
  width: number;
  height: number;
}

export interface CanvasSnapshot {
  viewBox: Rect | null;
  width: number | null;
  height: number | null;
  ratio: number;
}

const num = (raw: string | null): number | null => {
  const v = Number.parseFloat(raw ?? '');
  return Number.isFinite(v) ? v : null;
};

/** 读画布现状（viewBox 优先；没有 viewBox 时回落 width/height） */
export function readCanvas(root: Element): CanvasSnapshot {
  const raw = (root.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/).map(Number);
  const viewBox =
    raw.length === 4 && raw.every((n) => Number.isFinite(n))
      ? { x: raw[0] as number, y: raw[1] as number, w: raw[2] as number, h: raw[3] as number }
      : null;

  const width = num(root.getAttribute('width')) ?? viewBox?.w ?? null;
  const height = num(root.getAttribute('height')) ?? viewBox?.h ?? null;
  const ratio = width && height && height > 0 ? width / height : 1;
  return { viewBox, width, height, ratio };
}

const fmt = (n: number): string => String(Math.round(n * 1000) / 1000);

const MIN_SIZE = 1;

/**
 * 改画布尺寸：**只改画板（viewBox）**，min-x/min-y 与内容一律不动（留白语义）。
 * width/height 若原本存在则一并同步（否则保持"无 width/height"的响应式文档形态）。
 */
export function setCanvasSize(root: Element, size: CanvasSize): Command {
  const w = Math.max(MIN_SIZE, size.width);
  const h = Math.max(MIN_SIZE, size.height);
  const current = readCanvas(root);
  const minX = current.viewBox?.x ?? 0;
  const minY = current.viewBox?.y ?? 0;
  const patches: Patch[] = [];
  const path = pathOf(root, root) ?? [];
  const nextViewBox = `${fmt(minX)} ${fmt(minY)} ${fmt(w)} ${fmt(h)}`;
  const vbPatch = attrPatch(path, 'viewBox', root.getAttribute('viewBox'), nextViewBox);
  if (vbPatch) patches.push(vbPatch);

  if (root.hasAttribute('width')) {
    const p = attrPatch(path, 'width', root.getAttribute('width'), fmt(w));
    if (p) patches.push(p);
  }
  if (root.hasAttribute('height')) {
    const p = attrPatch(path, 'height', root.getAttribute('height'), fmt(h));
    if (p) patches.push(p);
  }
  // 离散操作（输入框 @change / 点预设）各成一条 undo，不需要 mergeKey
  return { label: `画布 ${fmt(w)}×${fmt(h)}`, patches };
}

export interface RatioPreset {
  label: string;
  ratio: number | null;
}

export const RATIO_PRESETS: readonly RatioPreset[] = [
  { label: '无限制', ratio: null },
  { label: '1:1', ratio: 1 },
  { label: '4:3', ratio: 4 / 3 },
  { label: '16:9', ratio: 16 / 9 },
];

/**
 * 锁着比例时改一边推另一边：`edited='width'` 按宽算高，`edited='height'` 按高算宽。
 * 结果取整：画布尺寸用整数最省心 —— 输入框显示的数就是文档里的数（不会 506.25 对不上 506）。
 */
export function applyRatio(size: CanvasSize, ratio: number, edited: 'width' | 'height'): CanvasSize {
  if (!Number.isFinite(ratio) || ratio <= 0) return size;
  return edited === 'width'
    ? { width: size.width, height: Math.max(MIN_SIZE, Math.round(size.width / ratio)) }
    : { width: Math.max(MIN_SIZE, Math.round(size.height * ratio)), height: size.height };
}

/**
 * 按比例预设改画布：**以宽为准**推高（同样只动画板，内容不缩放）。
 * 为什么是「以宽为准」而不是「以高为准」：宽是横向主量、也是面板里的第一个字段；
 * 反过来（保持高度）点 16:9 会把画布横向撑得很大，看着像「画布突然变宽了」。
 * ratio=null（无限制）**不是命令**，只是解锁 → 返回空命令。
 */
export function setCanvasRatio(root: Element, ratio: number | null): Command {
  const current = readCanvas(root);
  if (ratio === null || current.width === null || current.height === null) {
    return { label: '画布比例', patches: [] };
  }
  return setCanvasSize(root, applyRatio({ width: current.width, height: current.height }, ratio, 'width'));
}

/**
 * 背景色（写进文档的形态）：`<svg style="background-color: …">`。
 * 传 null 则移除该声明（其余 style 声明原样保留）。
 */
export function setCanvasBackground(root: Element, color: string | null): Command {
  const path = pathOf(root, root) ?? [];
  const before = root.getAttribute('style');
  // 其余声明原样保留，只增删 background-color
  const others = parseStyleAttr(before).filter(([prop]) => prop !== 'background-color');
  const decls: Array<[string, string]> =
    color === null || color === '' ? others : [...others, ['background-color', color]];
  const next = decls.length === 0 ? null : decls.map(([prop, val]) => `${prop}: ${val};`).join(' ');

  const patch = attrPatch(path, 'style', before, next);
  return { label: '画布背景', patches: patch ? [patch] : [] };
}

/** 当前文档背景色（没写就返回 null，交给编辑器视图默认值） */
export const canvasBackgroundOf = (root: Element): string | null =>
  styleValue(root.getAttribute('style'), 'background-color');
